import { useState, useEffect, useRef } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { Trash2, Minus, Plus } from "lucide-react";
import { useCart } from "../context/CartContext";
import { api, money, resolveImg } from "../lib/api";
import { ProductCard } from "../components/ProductCard";
import { toast } from "sonner";

export default function CartPage() {
  const { items, updateQty, removeItem, subtotal, restoreItems } = useCart();
  const [threshold, setThreshold] = useState(75);
  const [recs, setRecs] = useState([]);
  const [params, setParams] = useSearchParams();
  const recovered = useRef(false);

  useEffect(() => {
    api.get("/settings").then(({ data }) => setThreshold(data.free_shipping_threshold)).catch(() => {});
    api.get("/products?best_seller=true&limit=4").then(({ data }) => setRecs(data.products)).catch(() => {});
  }, []);

  useEffect(() => {
    const rid = params.get("recover");
    if (!rid || recovered.current) return;
    recovered.current = true;
    api.get(`/cart/snapshot/${rid}`).then(({ data }) => {
      restoreItems(data.items || []);
      sessionStorage.setItem("shodan_coupon", "COMEBACK10");
      toast.success("Your cart is back! Code COMEBACK10 (10% off) will be pre-filled at checkout.");
    }).catch(() => toast.error("That cart link has expired or was already recovered."));
    setParams({}, { replace: true });
  }, [params, setParams, restoreItems]);

  const remaining = threshold - subtotal;

  if (items.length === 0) {
    return (
      <div className="max-w-7xl mx-auto px-4 md:px-8 py-24 text-center">
        <h1 className="font-heading text-3xl uppercase font-bold tracking-tight">Your Cart Is Empty</h1>
        <p className="text-black/50 mt-2 text-sm">Gear for the moments you plan for — and the ones you don't.</p>
        <Link data-testid="empty-cart-shop-btn" to="/shop" className="inline-block mt-6 bg-[#4A5D23] text-white px-8 py-4 text-sm font-bold uppercase tracking-widest hover:bg-[#3D4D1D] transition-colors duration-150">Shop All Gear</Link>
        {recs.length > 0 && (
          <div className="mt-16 text-left">
            <h2 className="font-heading text-xl uppercase font-bold tracking-tight mb-6">Popular Gear</h2>
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">{recs.map((p) => <ProductCard key={p.slug} product={p} />)}</div>
          </div>
        )}
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto px-4 md:px-8 py-10">
      <h1 className="font-heading text-3xl md:text-4xl uppercase font-bold tracking-tight mb-8">Shopping Cart</h1>
      {remaining > 0 ? (
        <div data-testid="free-shipping-bar" className="border border-[#4A5D23]/30 bg-[#4A5D23]/5 px-5 py-3 mb-8">
          <p className="text-sm font-semibold">You're <span className="text-[#4A5D23] font-bold">{money(remaining)}</span> away from FREE shipping</p>
          <div className="h-1.5 bg-black/10 mt-2"><div className="h-full bg-[#4A5D23] transition-all duration-300" style={{ width: `${Math.min(100, (subtotal / threshold) * 100)}%` }} /></div>
        </div>
      ) : (
        <div data-testid="free-shipping-earned" className="border border-[#2E8B57]/30 bg-[#2E8B57]/5 px-5 py-3 mb-8 text-sm font-bold text-[#2E8B57] uppercase tracking-wider">You've earned free shipping</div>
      )}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-10">
        <div className="lg:col-span-2 space-y-px bg-black/10 border border-black/10 h-fit self-start">
          {items.map((item) => (
            <div key={`${item.item_type}-${item.slug}-${item.sku_code || ""}`} data-testid={`cart-item-${item.slug}${item.sku_code ? `-${item.sku_code}` : ""}`} className="bg-white p-4 flex gap-4 items-center">
              <img src={resolveImg(item.image)} alt={item.name} className="w-20 h-20 object-cover bg-[#F4F3F0] border border-black/5" />
              <div className="flex-1 min-w-0">
                <p className="font-heading font-bold text-sm uppercase tracking-tight truncate">{item.name}</p>
                {item.item_type === "kit" && <span className="text-[10px] font-bold uppercase tracking-widest text-[#4A5D23]">Ready Kit Bundle</span>}
                {item.sku_code && <span className="text-[10px] font-mono text-black/40 block">SKU {item.sku_code}</span>}
                {item.item_type === "kit" && item.variant_label && <span className="text-[10px] text-[#4A5D23] block">{item.variant_label}</span>}
                {item.item_type === "kit" && item.contents?.length > 0 && (
                  <div data-testid={`cart-kit-contents-${item.slug}`} className="mt-2 space-y-1 border-l-2 border-[#4A5D23]/30 pl-2">
                    {item.contents.map((c) => (
                      <div key={c.slug} className="flex items-center gap-2 text-[11px] text-black/60">
                        <img src={resolveImg(c.image)} alt={c.name} className="w-6 h-6 object-cover bg-[#F4F3F0] border border-black/5 shrink-0" />
                        <span className="truncate">{c.name}</span>
                      </div>
                    ))}
                  </div>
                )}
                <p className="text-sm font-bold mt-1">{money(item.price)}</p>
              </div>
              <div className="flex items-center border border-black/20">
                <button data-testid={`cart-qty-minus-${item.slug}${item.sku_code ? `-${item.sku_code}` : ""}`} onClick={() => updateQty(item.slug, item.item_type, item.quantity - 1, item.sku_code || "")} className="p-2 hover:bg-[#F4F3F0]" aria-label="Decrease"><Minus size={13} /></button>
                <span className="w-8 text-center text-sm font-bold">{item.quantity}</span>
                <button data-testid={`cart-qty-plus-${item.slug}${item.sku_code ? `-${item.sku_code}` : ""}`} onClick={() => updateQty(item.slug, item.item_type, item.quantity + 1, item.sku_code || "")} className="p-2 hover:bg-[#F4F3F0]" aria-label="Increase"><Plus size={13} /></button>
              </div>
              <button data-testid={`cart-remove-${item.slug}${item.sku_code ? `-${item.sku_code}` : ""}`} onClick={() => removeItem(item.slug, item.item_type, item.sku_code || "")} aria-label="Remove" className="text-black/40 hover:text-[#8B0000] transition-colors duration-150"><Trash2 size={17} /></button>
            </div>
          ))}
        </div>
        <div className="border border-black/10 p-6 h-fit bg-[#F4F3F0]">
          <h3 className="font-heading font-bold uppercase tracking-tight mb-4">Order Summary</h3>
          <div className="space-y-2 text-sm">
            <div className="flex justify-between"><span className="text-black/60">Subtotal</span><span data-testid="cart-subtotal" className="font-bold">{money(subtotal)}</span></div>
            <div className="flex justify-between"><span className="text-black/60">Shipping</span><span>{remaining > 0 ? "Calculated at checkout" : "FREE"}</span></div>
          </div>
          <Link data-testid="checkout-btn" to="/checkout" className="block text-center mt-6 bg-[#4A5D23] text-white py-4 text-sm font-bold uppercase tracking-widest hover:bg-[#3D4D1D] transition-colors duration-150">Proceed to Checkout</Link>
          <Link to="/shop" className="block text-center mt-3 text-xs font-bold uppercase tracking-widest text-black/60 hover:text-black">Continue Shopping</Link>
        </div>
      </div>
      {recs.length > 0 && (
        <div className="mt-16">
          <h2 className="font-heading text-xl uppercase font-bold tracking-tight mb-6">You Might Also Need</h2>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">{recs.map((p) => <ProductCard key={p.slug} product={p} />)}</div>
        </div>
      )}
    </div>
  );
}
